import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './problem';

/**
 * Retrying is only ever right when the failure might not happen again.
 *
 * A 401/403/409 is a settled answer: the session is gone, the plan does not include it, the cap is
 * reached. Retrying those wastes the user's time and, worse, multiplies a failed write into several
 * attempts — which for a document-numbering endpoint would burn numbers nobody asked for.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    // 5xx and 408/429 are worth another go; every deliberate refusal is not.
    const worthRetrying = error.status >= 500 || error.status === 408 || error.status === 429;

    return worthRetrying && failureCount < 2;
  }

  // A NetworkError means the request never arrived — the one case retrying genuinely helps.
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,

        // Permissions and entitlements are cached server-side for about ten minutes; the client
        // must not hold anything longer, or a revoked grant keeps working in the UI after the API
        // has already stopped honouring it. One minute leaves plenty of headroom.
        staleTime: 60_000,

        // ERP users leave tabs open all day. Refetching when they come back is what stops a stale
        // stock figure being read as current.
        refetchOnWindowFocus: true,
      },

      mutations: {
        // Never automatically. A retried write is a duplicate document, and the user is present to
        // decide whether to try again.
        retry: false,
      },
    },
  });
}
